import { useRef } from 'react'
import ChatAnimation, { StyledChatAnimationWrapper } from '../../components/ChatAnimation'
import ChatInput from '../../components/ChatInput'
import SettingsPortal from '../../components/SettingsPortal'
import { StyledBody, StyledChatHeader, StyledRoot } from '../Cortext/Cortext'
import { useDatura } from './useDatura'
import { FormikProvider } from 'formik'
import { StyledRow, StyledSettings } from '../Cortext/Settings/SettingsStyles.css'
import {
    dateFilterTypesOptions,
    multiSelectOptions,
    responseOrderOptions,
    searchModelOptions,
} from './types'
import LoadingDots, { LoadingDotsBox } from 'components/Loader/LoadingDots'
import ChatMarkdown from 'pages/Search/Markdown/ChatMarkdown'
import SubnetDescription from '../../components/SubnetDescription'
import DropdownFormik from 'components/DropdownFormik'
import Examples from '../../components/Examples'
import { examples } from './constants'
import ChatHistoryList from '../../components/History/ChatHistoryList'
import { StyledHorizontalDivider } from 'routes/ChatRouteLayout'
import TypographyPrimary from 'components/Typography/Primary'
import CopyButton from 'components/CopyButton'
import LabeledCopyButton from 'components/CopyButton/LabeledCopyButton'
import styled from 'styled-components'
import { StyledDivider, StyledResponse } from '../../components/TextChatWindow/TextChatWindow'

const Datura = () => {
    const daturaRef = useRef(null)

    const {
        formik,
        isLoading,
        chat,
        handleStartNewChat,
        handleDeleteHistory,
        chatHistory,
        setChat,
        scrollRef,
        displayPrompt,
        requestLoading,
        handleStopRequest,
    } = useDatura()

    const reversedMessages = Array.isArray(chat?.messages) ? [...chat.messages].reverse() : []

    return (
        <FormikProvider value={formik}>
            <StyledRoot ref={daturaRef}>
                <ChatHistoryList
                    onNewChat={handleStartNewChat}
                    chatHistory={chatHistory}
                    handleDeleteHistory={handleDeleteHistory}
                    setChat={setChat}
                    chat={chat}
                />

                <StyledBody>
                    <StyledChatHeader />

                    <StyledResponse>
                        <div ref={scrollRef} />
                        <StyledDivider />

                        {isLoading && (
                            <StyledContainer>
                                {chat.messages.length > 0 && <StyledHorizontalDivider />}

                                <StyledRow>
                                    <TypographyPrimary
                                        value={displayPrompt}
                                        size={'x-large'}
                                        bold
                                    />
                                    <CopyButton value={displayPrompt} />
                                </StyledRow>
                                <LoadingDotsBox>
                                    <LoadingDots large />
                                </LoadingDotsBox>
                            </StyledContainer>
                        )}

                        {reversedMessages?.length > 0 ? (
                            reversedMessages?.map((message, index) => {
                                return (
                                    <StyledContainer key={index}>
                                        {index < reversedMessages.length - 1 && (
                                            <StyledHorizontalDivider />
                                        )}

                                        <StyledRow>
                                            <TypographyPrimary
                                                value={message?.prompt || ''}
                                                size={'x-large'}
                                                bold
                                            />
                                            <CopyButton value={message?.prompt || ''} />
                                        </StyledRow>

                                        <ChatMarkdown key={index}>
                                            {message.message || ''}
                                        </ChatMarkdown>

                                        <StyledRow displayEnd>
                                            <LabeledCopyButton
                                                value={message?.curlString || ''}
                                                label='Copy cURL'
                                            />
                                            <LabeledCopyButton
                                                value={message.message || ''}
                                                label='Copy'
                                            />
                                        </StyledRow>
                                    </StyledContainer>
                                )
                            })
                        ) : (
                            <>
                                {!isLoading && (
                                    <StyledChatAnimationWrapper>
                                        <ChatAnimation />
                                    </StyledChatAnimationWrapper>
                                )}
                            </>
                        )}
                    </StyledResponse>

                    <ChatInput
                        formik={formik}
                        fieldName='message'
                        stopRequest={{ requestLoading, handleStopRequest }}
                    />
                </StyledBody>
            </StyledRoot>

            <SettingsPortal portalRef={daturaRef}>
                <StyledSettings>
                    <SubnetDescription
                        noImage
                        title={'22. Datura'}
                        description={'Smart scrape search on twitter, Reddit and other platforms.'}
                        docUrl={`${import.meta.env.REACT_APP_SUBNET_DOCS_URL}/datura`}
                    />

                    <Examples
                        disabled={requestLoading}
                        formik={formik}
                        chatInputName={'message'}
                        examples={examples}
                    />

                    {/* <DropdownFormik
                        label={'Synapse'}
                        fieldName={'synapse_type'}
                        fieldValue={formik.values.synapse_type}
                        options={synapseOptions}
                        setFieldValue={formik.setFieldValue}
                    /> */}

                    <DropdownFormik
                        isMulti
                        label={'Tools'}
                        fieldName={'tools'}
                        fieldValue={formik.values.tools}
                        options={multiSelectOptions}
                        setFieldValue={formik.setFieldValue}
                    />

                    <DropdownFormik
                        label={'Date filter'}
                        fieldName={'date_filter_type'}
                        fieldValue={formik.values.date_filter_type}
                        options={dateFilterTypesOptions}
                        setFieldValue={formik.setFieldValue}
                    />

                    <DropdownFormik
                        label={'Response order'}
                        fieldName={'response_order'}
                        fieldValue={formik.values.response_order}
                        options={responseOrderOptions}
                        setFieldValue={formik.setFieldValue}
                    />

                    <DropdownFormik
                        label={'Model'}
                        fieldName={'model'}
                        fieldValue={formik.values.model}
                        options={searchModelOptions}
                        setFieldValue={formik.setFieldValue}
                    />
                </StyledSettings>
            </SettingsPortal>
        </FormikProvider>
    )
}

export default Datura

const StyledContainer = styled.div`
    display: flex;
    flex-direction: column;

    gap: 20px;
`
